import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { Box } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';
import useEventListener from '../../hooks/useEventListener.hooks';
import useIssueFlagWindow from '../../hooks/issueReport/useIssueFlagWindow.hooks';
import useIssueReport from '../../hooks/issueReport/useIssueReport.hooks';
import Colors from '../../utils/color';
import { focusOmniBar } from '../../utils/omniBarFocus';
import {
  HITL3_FONT_SIZE,
  HITL3_TITLE_FONT_SIZE,
} from '../../utils/hitl3Typography';
import FlagWindowDial from './FlagWindowDial';
import { DISCARD_BUTTON_SX, LATER_BUTTON_SX } from './issueActionButtonStyle';
import IssueClassifyBody, { IssueClassifyHandle } from './IssueClassifyBody';
import { shortId } from './issueReportFormat';
import { ISSUE_PANEL_Z_INDEX } from './issueReportLayers';

/**
 * HITL-1501: confirmation that a flag landed, and the first chance to classify it.
 *
 * HITL-1573: no longer the ONLY chance. The panel used to be terminal — a report not
 * classified here stayed `Unclassified` for good — and every choice in it was shaped
 * by that: the 10s window rather than 5s, the clock pausing for the detail field, the
 * whole taxonomy on screen at once instead of behind a drill-in. Those all stay,
 * because classifying while it is fresh is still the best outcome. What changes is the
 * cost of not managing it: `Later` is now a real exit, the report goes to the
 * outstanding backlog, and the agent picks it up from the Issue button (HITL-1574) or
 * the task-router lobby (HITL-1575).
 *
 * ## What lives here, and what does not
 *
 * HITL-1572 split this along one line: *is this about the record, or about the
 * 10-second window?* The record-shaped half — the detail field, the chips, seed
 * handling, type preservation, commit-on-unmount and the Enter gate — is
 * `IssueClassifyBody`, mounted by the deferred surfaces too. What stays here is the
 * WINDOW: the dial, pause/resume, the window-capture Escape, the `focusOmniBar()`
 * handback, `Later`/`Discard`, and this panel's own geometry and markers.
 *
 * ## Not a FloatingBanner
 *
 * That component is a one-line flex row in a fixed slot shared with RefreshBanner and
 * TaskRouterOnBanner. Growing it into a panel for one caller would drift the other
 * two, so this owns its own geometry — while deliberately keeping the family's slot
 * (`top: 100px`, centred, daintree), because that is where the agent's eye already
 * goes for a screen-level notice.
 *
 * ## Non-modal by construction
 *
 * Absolutely positioned, no MUI Modal, no backdrop, no focus trap. No ordering control
 * is ever blocked or delayed.
 *
 * `data-issue-report="true"` sits on the ROOT and that is load-bearing: the
 * agent-interception guards listen on `document` in the CAPTURE phase and match with
 * `target.closest()`, so the marker must be an ANCESTOR of the real event target —
 * a chip's inner text node, the icon svg, the input. Marking anything deeper would
 * pop the "would you like to take over?" dialog on every classification click and
 * every keystroke in the detail field.
 *
 * `Discard` emits a RETRACTION at the next revision; it does not cancel the revision-0
 * send. Committing on press is the point — a refresh, a car exit or a network blip
 * inside the window would otherwise lose the flag, and blips are exactly what agents
 * flag.
 */
const IssueFlagPanel = () => {
  const {
    voidIssue,
    pauseFlagWindow,
    resumeFlagWindow,
    closeFlagWindow,
    activeReport,
  } = useIssueReport();
  const flagWindow = useIssueFlagWindow();
  /** `Discard` has to tell the body to drop its pending detail — see `handleDiscard`. */
  const bodyRef = useRef<IssueClassifyHandle>(null);

  // Whether the countdown is currently BANKED. The body reports the first keystroke
  // of each typing episode and every blur; this is what stops a blur with nothing
  // typed from resuming a clock that never paused.
  //
  // Focus cannot be the trigger: the field is focused automatically when the panel
  // opens, so pausing on focus would mean the countdown never ran and every panel
  // lived to the 60s hard cap.
  const pausedRef = useRef(false);

  const issueId = flagWindow?.issueId ?? null;
  // The Escape listener is registered unconditionally (hooks cannot be conditional),
  // so it reads the live id from a ref rather than closing over a stale one.
  const flagWindowRef = useRef<string | null>(null);
  flagWindowRef.current = issueId;

  /**
   * One window, one pause state — reset per window id rather than on unmount, because
   * this component never unmounts; it renders null between flags.
   *
   * The cleanup hands the caret back. The typed detail is NOT recorded here: that is
   * the body's unmount commit, which fires in the same teardown.
   */
  useEffect(() => {
    pausedRef.current = false;
    if (!issueId) return undefined;
    return () => {
      // Hand the caret straight back, so the agent can type the order the instant
      // the panel goes. Without this it sits on <body> until some unrelated
      // re-render happens to run the omnibar's own refocus.
      //
      // Deferred a tick, symmetrically with how the body claims it: the panel's DOM
      // is torn down around this cleanup, and focusing while the currently-focused
      // node is being removed leaves the caret on <body> anyway.
      setTimeout(focusOmniBar, 0);
    };
  }, [issueId]);

  /**
   * The one exit, shared by `Later` and Escape.
   *
   * ZERO commit logic of its own: the body commits a typed detail on unmount, with the
   * type preserved, however the panel closes. So closing is just closing.
   */
  const closePanel = useCallback(() => {
    pausedRef.current = false;
    closeFlagWindow();
  }, [closeFlagWindow]);

  /**
   * Escape is a SINGLE press — one press always closes, from anywhere, with the typed
   * detail committed. It is exactly `Later` on the keyboard.
   *
   * It used to be two-stage: with focus in the detail field the first press blurred
   * and resumed the clock, and only a second press closed, because a single Escape
   * destroyed a half-typed detail and closing was terminal. Neither half of that
   * holds now. The detail is not destroyed — the body commits it on the way out — and
   * the report is not stranded, because it lands in the outstanding backlog. All the
   * second stage bought was a press that looked like it had done nothing.
   *
   * WINDOW CAPTURE, not a React onKeyDown on the panel. A React handler only fires
   * when focus is already inside the panel, and it almost never is: the omnibar
   * refocuses itself on every render, so Escape would be delivered there and the
   * panel could never be dismissed by keyboard. Window capture also runs before
   * Autocomplete's own `clearOnEscape`, so dismissing the panel cannot wipe a
   * half-typed order.
   *
   * Because it captures on `window` and stops propagation, it would otherwise hold
   * Escape hostage for as long as the panel is open — including from an unrelated
   * modal that happens to be up (the take-over prompt). So it stands down whenever a
   * dialog is in the DOM, the same check `useIntentHotkeys` and `LiveTranscriptCard`
   * already make. The panel is `role="region"`, never a dialog, so this cannot match
   * the panel itself; and the panel still closes on its own clock, so standing down
   * cannot strand it.
   */
  const handleEscape = (event: Event) => {
    const e = event as KeyboardEvent;
    if (e.key !== 'Escape' || !flagWindowRef.current) return;
    if (
      typeof document !== 'undefined' &&
      document.querySelector('[role="dialog"]')
    ) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    closePanel();
  };

  useEventListener('keydown', handleEscape, window, true);

  const handleDetailFirstInput = useCallback(() => {
    pausedRef.current = true;
    pauseFlagWindow();
  }, [pauseFlagWindow]);

  const handleDetailBlur = useCallback(() => {
    if (!pausedRef.current) return;
    pausedRef.current = false;
    resumeFlagWindow();
  }, [resumeFlagWindow]);

  const handleDiscard = useCallback(() => {
    const id = flagWindowRef.current;
    if (!id) return;
    // Discard retracts the whole report, so a typed detail must not be re-sent by the
    // body's unmount commit afterwards. The latch lives with the text it guards.
    bodyRef.current?.discard();
    voidIssue(id);
  }, [voidIssue]);

  /**
   * The full id to the clipboard, because the reason to show one at all is
   * cross-referencing with support and the logs — and a truncated id cannot do that.
   *
   * Best effort by design: `navigator.clipboard` is absent outside a secure context
   * and can reject when the document is not focused. A failed copy must not throw
   * inside a panel the agent is using to report a problem, and the full value is on
   * the `title` either way, so there is nothing to recover.
   */
  const handleCopyId = useCallback(() => {
    const id = flagWindowRef.current;
    if (!id) return;
    navigator.clipboard?.writeText(id).catch(() => undefined);
  }, []);

  // AFTER every hook — an early return above any of them changes the hook count
  // between renders, which React rejects outright.
  //
  // `!activeReport` is a type narrowing, not a second condition: `useIssueFlagWindow`
  // derives its window from the active report, so the two are null together.
  if (!flagWindow || !activeReport) return null;

  return (
    <Box
      data-issue-report="true"
      // Declares this panel the caret owner for as long as it is open, so the
      // omnibar's click handback stands down underneath it. Cannot be inferred:
      // the panel is role="region" (no focus trap for a dialog check to find), and
      // clicking its heading leaves activeElement on <body>. See
      // CARET_OWNER_SELECTOR.
      data-caret-owner="true"
      data-testid="issue-flag-panel"
      role="region"
      aria-label="Issue reported"
      sx={{
        position: 'absolute',
        top: '100px',
        left: '50%',
        transform: 'translateX(-50%)',
        // 860, not the 760 this shipped with: at 760 the chip rows had 628px of
        // usable width after the 16px padding either side, the 92px category label
        // column and its 8px gap — and AI RESPONSE needs about 704px for its five
        // values, so it wrapped to a second row. 860 gives 728px, which clears it
        // and leaves AI ORDER (the next longest, ~608px) comfortable.
        //
        // Widened rather than tightening the chips: their padding is the touch
        // target, and the label column cannot shrink because "AI Escalation" already
        // nearly fills it. The `calc` arm still governs below ~890px viewports,
        // where wrapping resumes as graceful degradation.
        width: 'min(860px, calc(100% - 32px))',
        backgroundColor: Colors.daintree,
        color: Colors.white,
        borderLeft: `4px solid ${Colors.green}`,
        p: 2,
        zIndex: ISSUE_PANEL_Z_INDEX,
        boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
      }}
    >
      {/* ONE action row: what happened, which report, and every way out of the panel.
          The prior-flags summary line that used to sit under the title is gone —
          this session's history is reachable by hovering the Issue button, and the
          explicit id says which report this panel is about far more precisely than
          "for this order" ever did. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          // The row is as tall as the dial. Deliberate: the deadline is the one thing
          // in here the agent cannot recover from missing.
          minHeight: 56,
          // …which means the dial's ring IS the bottom edge of this row, with nothing
          // between it and the detail field: `IssueClassifyBody` starts flush at its
          // top, because its other two hosts space it themselves. Without this the
          // ring sat directly on the field's border and read as overlapping it. 1.5
          // matches the body's own input-to-chips gap, so the panel keeps one rhythm.
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: HITL3_TITLE_FONT_SIZE,
            fontWeight: 600,
            lineHeight: 1.3,
            flexShrink: 0,
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: Colors.green }} />
          Issue Reported
        </Box>

        {/* Not a <button>, and that is a considered trade-off rather than an
            oversight: making it focusable would put a copy affordance ahead of
            `Later` and `Discard` in the tab order, and inside a 10s window the first
            tab stop should be an action. The id itself is plain selectable text and
            the full value is on the title, so nothing is unreachable by keyboard —
            only the one-click shortcut is. */}
        <Box
          component="span"
          onClick={handleCopyId}
          title={flagWindow.issueId}
          data-testid="issue-report-id"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            flexShrink: 0,
            px: '5px',
            py: '1px',
            borderRadius: '3px',
            fontFamily: 'monospace',
            fontSize: HITL3_FONT_SIZE,
            color: 'rgba(255,255,255,0.62)',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.08)',
              color: 'rgba(255,255,255,0.86)',
            },
          }}
        >
          ID {shortId(flagWindow.issueId)}
          <ContentCopyRoundedIcon sx={{ fontSize: 12 }} />
        </Box>

        {/* Pushes the controls to the trailing edge, away from the title, so the two
            halves of the row do not read as one sentence. */}
        <Box sx={{ flex: 1, minWidth: 0 }} />

        {/* `Later` FIRST, and always rendered — including on a report that arrived
            pre-classified from a transcript gesture, because an ASR type is a hint
            the agent has not confirmed yet, so those are not settled either.

            It is a labelled, mouse-reachable, discoverable Escape. Its real job is
            that it is the only place in the product where an agent learns the backlog
            exists at all. */}
        <Box
          component="button"
          type="button"
          onClick={closePanel}
          data-testid="issue-later"
          sx={LATER_BUTTON_SX}
        >
          Later
        </Box>

        <Box
          component="button"
          type="button"
          onClick={handleDiscard}
          data-testid="issue-discard"
          sx={DISCARD_BUTTON_SX}
        >
          Discard
        </Box>

        <FlagWindowDial
          expiresAt={flagWindow.expiresAt}
          remainingMs={flagWindow.remainingMs}
          isPaused={flagWindow.isPaused}
        />
      </Box>

      <IssueClassifyBody
        // One window, one body. The panel renders null between flags so this would
        // remount anyway; keying on the window makes the reset structural rather
        // than something the body's effect has to remember to do.
        key={flagWindow.issueId}
        ref={bodyRef}
        record={activeReport}
        surface="flag_panel"
        autoFocusDetail
        // The panel is the surface with a clock to race, so it is the one surface
        // where a sending key earns its keep.
        enterSubmits
        onCommitted={closeFlagWindow}
        onDetailFirstInput={handleDetailFirstInput}
        onDetailBlur={handleDetailBlur}
      />
    </Box>
  );
};

export default IssueFlagPanel;
